/**
 * ISBN Scanner Component
 * 
 * Standalone ISBN barcode scanner for parent-facing flows.
 * Uses the same scanning approach as UnifiedScanner:
 * 1. Native BarcodeDetector API first (instant, no artifacts)
 * 2. html5-qrcode fallback for older browsers
 * 
 * This ensures visual and functional parity between parent and admin scanners.
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X, Loader2, Keyboard } from 'lucide-react';
import { colors } from '@/lib/design';
import { ISBN_SCANNER_CONFIG } from '@/lib/scan-config';

// Check for native BarcodeDetector support (instant ISBN scanning)
const hasNativeBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

// ============================================================================
// Types
// ============================================================================

interface IsbnScannerProps {
  /** Called when ISBN is successfully detected */
  onIsbnDetected: (isbn: string) => void;
  /** Called when user wants to close/cancel */
  onClose: () => void;
  /** Called when user wants to enter manually instead */
  onManualEntry?: () => void;
}

type ScannerState = 'initializing' | 'scanning' | 'error';

// ============================================================================
// Component
// ============================================================================

export function IsbnScanner({ onIsbnDetected, onClose, onManualEntry }: IsbnScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const barcodeDetectorRef = useRef<BarcodeDetector | null>(null);
  const scanIntervalRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const processingRef = useRef(false);
  
  const [state, setState] = useState<ScannerState>('initializing');
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [lastDetected, setLastDetected] = useState<string | null>(null);

  // ==========================================================================
  // Cleanup
  // ==========================================================================
  
  const cleanup = useCallback(() => {
    // Stop native scanner interval
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    barcodeDetectorRef.current = null;
    
    // Stop html5-qrcode
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          html5QrCodeRef.current.stop().catch(() => {});
        }
      } catch (e) {
        // Ignore cleanup errors
      }
      html5QrCodeRef.current = null;
    }
    
    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // ==========================================================================
  // ISBN Validation & Processing
  // ==========================================================================
  
  const isValidIsbn = useCallback((code: string): boolean => {
    const clean = code.replace(/[-\s]/g, '');
    // ISBN-10: 9 digits + check digit (0-9 or X)
    // ISBN-13: 13 digits
    return /^(\d{10}|\d{13}|\d{9}X)$/i.test(clean);
  }, []);

  const handleIsbnFound = useCallback((isbn: string) => {
    if (processingRef.current) return;
    processingRef.current = true;
    
    const cleanIsbn = isbn.replace(/[-\s]/g, '');
    console.log('[IsbnScanner] ISBN detected:', cleanIsbn);
    
    // Haptic feedback if available
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
    
    cleanup();
    onIsbnDetected(cleanIsbn);
  }, [cleanup, onIsbnDetected]);

  // ==========================================================================
  // Camera Initialization
  // ==========================================================================
  
  const initCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      
      if (!isMountedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      // Start scanning
      startScanning();
    } catch (err: any) {
      console.error('[IsbnScanner] Camera error:', err);
      if (isMountedRef.current) {
        setError(err.name === 'NotAllowedError' 
          ? 'Camera access denied. Please allow camera access and try again.'
          : 'Could not access camera. Please try manual entry.');
        setState('error');
      }
    }
  }, []);

  // ==========================================================================
  // Native BarcodeDetector Scanning
  // ==========================================================================
  
  const startNativeScanning = useCallback(() => {
    if (!hasNativeBarcodeDetector || !videoRef.current) return false;
    
    try {
      const detector = new BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e']
      });
      barcodeDetectorRef.current = detector;
      
      // Scan at 30fps for instant detection
      scanIntervalRef.current = window.setInterval(async () => {
        if (!barcodeDetectorRef.current || !videoRef.current || processingRef.current) return;
        
        try {
          const barcodes = await barcodeDetectorRef.current.detect(videoRef.current);
          if (barcodes.length > 0) {
            const rawValue = barcodes[0].rawValue;
            setLastDetected(rawValue);
            
            if (isValidIsbn(rawValue)) {
              handleIsbnFound(rawValue);
            }
          }
        } catch (e) {
          // Detection failed this frame - expected, continue
        }
      }, 33); // ~30fps
      
      console.log('[IsbnScanner] Native BarcodeDetector active');
      return true;
    } catch (err) {
      console.log('[IsbnScanner] Native scanner not available:', err);
      return false;
    }
  }, [isValidIsbn, handleIsbnFound]);

  // ==========================================================================
  // html5-qrcode Fallback Scanning
  // Uses html5-qrcode video stream with HD resolution
  // ==========================================================================
  
  const startFallbackScanning = useCallback(async () => {
    // CRITICAL: Stop the original camera stream before html5-qrcode starts its own.
    // html5QrCode.start() requests a new getUserMedia stream internally.
    // Without this, we'd have two active streams - battery drain and resource waste.
    if (streamRef.current) {
      console.log('[IsbnScanner] Stopping original stream before fallback');
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setUsingFallback(true);
    
    const scannerId = 'isbn-fallback-scanner';
    const container = document.getElementById(scannerId);
    
    if (!container) {
      console.error('[IsbnScanner] Fallback container not found');
      setError('Scanner initialization failed');
      setState('error');
      return;
    }
    
    try {
      // Configure fallback scanner with same barcode formats as native detector
      const html5QrCode = new Html5Qrcode(scannerId, { 
        verbose: false,
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
        ],
        // Enable native BarcodeDetector when available for better performance
        useBarCodeDetectorIfSupported: true,
      });
      html5QrCodeRef.current = html5QrCode;
      
      // html5-qrcode API:
      // - First param: simple camera selector (ONE key only)
      // - Second param: config with videoConstraints for HD resolution
      await html5QrCode.start(
        ISBN_SCANNER_CONFIG.CAMERA_CONFIG,
        {
          fps: ISBN_SCANNER_CONFIG.FPS,
          // HD resolution via videoConstraints - dramatically improves detection distance
          videoConstraints: ISBN_SCANNER_CONFIG.VIDEO_CONSTRAINTS,
          // NO qrbox = scan FULL frame = maximum detection area
          disableFlip: true,
        },
        (decodedText) => {
          setLastDetected(decodedText);
          
          if (isValidIsbn(decodedText)) {
            handleIsbnFound(decodedText);
          }
        },
        () => {} // Ignore scan failures
      );
      
      // Log camera settings after scanner starts
      try {
        const settings = html5QrCode.getRunningTrackSettings() as Record<string, unknown>;
        console.log(`[IsbnScanner] Fallback camera: ${settings.width}x${settings.height}`);
      } catch {
        // Ignore if settings not available
      }
      
      console.log('[IsbnScanner] html5-qrcode fallback active (HD resolution, full frame)');
    } catch (err) {
      console.error('[IsbnScanner] Fallback scanner error:', err);
      setError('Could not start barcode scanner');
      setState('error');
    }
  }, [isValidIsbn, handleIsbnFound]);

  // ==========================================================================
  // Start Scanning (tries native first, then fallback)
  // ==========================================================================
  
  const startScanning = useCallback(() => {
    setState('scanning');
    
    // Try native BarcodeDetector first
    const nativeStarted = startNativeScanning();
    
    if (!nativeStarted) {
      // Fall back to html5-qrcode
      startFallbackScanning();
    }
  }, [startNativeScanning, startFallbackScanning]);

  // ==========================================================================
  // Initialize on mount
  // ==========================================================================
  
  useEffect(() => {
    initCamera();
  }, [initCamera]);

  // ==========================================================================
  // Render
  // ==========================================================================
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-[#1A1A1A]">Scan ISBN</h2>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-black/[0.05] transition-colors"
        >
          <X className="w-5 h-5 text-[#3D3D3D]" />
        </button>
      </div>

      {/* Scanner View - compact square for close-up barcode scanning */}
      <div className="relative w-full max-w-xs mx-auto aspect-square rounded-2xl overflow-hidden bg-black">
        {/* Native scanner uses our video element */}
        {!usingFallback && (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
            autoPlay
          />
        )}
        
        {/* Fallback scanner container (html5-qrcode creates its own video) */}
        <div 
          id="isbn-fallback-scanner" 
          className={`absolute inset-0 ${usingFallback ? '' : 'hidden'} [&_video]:!object-cover [&>div]:!bg-transparent`}
        />
        
        {/* Loading overlay */}
        {state === 'initializing' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 text-[#0A84FF] animate-spin" />
              <span className="text-[#1A1A1A] text-sm">Starting camera...</span>
            </div>
          </div>
        )}
        
        {/* Error overlay */}
        {state === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-20 p-6">
            <div className="text-center">
              <p className="text-[#3D3D3D] text-sm mb-4">{error}</p>
              {onManualEntry && (
                <button
                  onClick={onManualEntry}
                  className="px-4 py-2 rounded-xl bg-[#0A84FF] text-[#1A1A1A] text-sm font-medium"
                >
                  Enter Manually
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Scan frame overlay (only when scanning) */}
        {state === 'scanning' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="w-48 h-32 relative">
              {/* Corner accents - matches UnifiedScanner style */}
              <div 
                className="absolute -top-0.5 -left-0.5 w-8 h-8 border-t-[3px] border-l-[3px] rounded-tl-lg" 
                style={{ borderColor: colors.brand.primary }} 
              />
              <div 
                className="absolute -top-0.5 -right-0.5 w-8 h-8 border-t-[3px] border-r-[3px] rounded-tr-lg" 
                style={{ borderColor: colors.brand.primary }} 
              />
              <div 
                className="absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-[3px] border-l-[3px] rounded-bl-lg" 
                style={{ borderColor: colors.brand.primary }} 
              />
              <div 
                className="absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-[3px] border-r-[3px] rounded-br-lg" 
                style={{ borderColor: colors.brand.primary }} 
              />
              
              {/* Scanning line animation */}
              <div 
                className="absolute inset-x-2 h-0.5 animate-pulse"
                style={{ 
                  top: '50%', 
                  backgroundColor: colors.brand.primary,
                  boxShadow: `0 0 8px ${colors.brand.primary}`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Status */}
      <div className="text-center">
        {state === 'initializing' && (
          <p className="text-sm text-[#888888]">Requesting camera access...</p>
        )}
        {state === 'scanning' && (
          <div className="space-y-1">
            <p className="text-[#1A1A1A] font-medium flex items-center justify-center gap-2">
              <span className="w-2 h-2 bg-[#30D158] rounded-full animate-pulse" />
              {usingFallback ? 'Scanning...' : 'Camera active'}
            </p>
            <p className="text-sm text-[#888888]">
              Position the ISBN barcode within the frame
            </p>
            {lastDetected && !isValidIsbn(lastDetected) && (
              <p className="text-xs text-[#D4D4D4] mt-1">
                Detected: {lastDetected} (not a valid ISBN)
              </p>
            )}
          </div>
        )}
      </div>

      {/* Manual entry option */}
      {onManualEntry && state !== 'error' && (
        <button
          onClick={() => {
            cleanup();
            onManualEntry();
          }}
          className="w-full py-3 text-[#0A84FF] text-sm font-medium flex items-center justify-center gap-2"
        >
          <Keyboard className="w-4 h-4" />
          Enter manually instead
        </button>
      )}
    </div>
  );
}

export type { IsbnScannerProps };
